import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = "w-5 h-5" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        onClick={() => interactive && onRate?.(star)}
        className={interactive ? "cursor-pointer" : "cursor-default"}
        disabled={!interactive}
        data-testid={`star-${star}`}
      >
        <Star
          className={`${size} transition-colors ${
            star <= rating ? "text-neon-yellow fill-neon-yellow" : "text-zinc-600"
          }`}
        />
      </button>
    ))}
  </div>
);

export const ReviewsSection = ({ itemType, itemId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [itemType, itemId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/${itemType}/${itemId}`);
      setReviews(res.data.reviews);
      setAvgRating(res.data.avg_rating);
      setTotal(res.data.total);
      if (user) {
        setHasReviewed(res.data.reviews.some(r => r.user_id === user.user_id));
      }
    } catch (e) {
      console.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newRating === 0) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/reviews`, {
        item_type: itemType, item_id: itemId, rating: newRating, comment: newComment
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setReviews(prev => [res.data, ...prev]);
      setTotal(prev => prev + 1);
      setAvgRating(prev => prev === 0 ? newRating : Math.round(((prev * (total)) + newRating) / (total + 1) * 10) / 10);
      setNewRating(0);
      setNewComment("");
      setHasReviewed(true);
      toast.success("Review submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      setTotal(prev => prev - 1);
      setHasReviewed(false);
      toast.success("Review deleted");
    } catch (e) {
      toast.error("Failed to delete review");
    }
  };

  return (
    <div className="mt-10" data-testid="reviews-section">
      <div className="flex items-center gap-4 mb-6">
        <h2 className="font-heading text-xl uppercase tracking-wide">Reviews</h2>
        {total > 0 && (
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(avgRating)} />
            <span className="font-mono text-neon-yellow">{avgRating}</span>
            <span className="text-zinc-500 text-sm">({total})</span>
          </div>
        )}
      </div>

      {/* Write Review Form */}
      {user && !hasReviewed && (
        <form onSubmit={handleSubmit} className="glass p-6 mb-6" data-testid="review-form">
          <h3 className="font-medium mb-3">Write a Review</h3>
          <div className="mb-4">
            <StarRating rating={newRating} onRate={setNewRating} interactive />
          </div>
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="bg-black/50 border-white/10 mb-4"
            data-testid="review-comment-input"
          />
          <Button type="submit" disabled={submitting || newRating === 0} className="bg-neon-cyan text-black" data-testid="submit-review-button">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit Review
          </Button>
        </form>
      )}

      {!user && (
        <p className="text-zinc-500 text-sm mb-6">Login to write a review</p>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-zinc-500 text-center py-8">No reviews yet. Be the first!</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.review_id} className="glass p-5" data-testid={`review-${review.review_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-zinc-800 rounded-full overflow-hidden flex items-center justify-center text-xs text-zinc-500">
                    {review.user_picture ? (
                      <img src={review.user_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      review.user_name?.charAt(0)?.toUpperCase() || "?"
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{review.user_name}</p>
                    <StarRating rating={review.rating} size="w-3 h-3" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                  {user && (user.user_id === review.user_id || user.is_admin) && (
                    <button onClick={() => handleDelete(review.review_id)} className="text-xs text-zinc-500 hover:text-red-500" data-testid={`delete-review-${review.review_id}`}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
              {review.comment && <p className="text-sm text-zinc-300 mt-3">{review.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
